import React from 'react';

const PrivacyPolicyPage: React.FC = () => {
  return (
    <div className="max-w-6xl mx-auto py-12 px-4 sm:px-6 lg:px-8 min-h-screen">
      <h1 className="text-4xl font-extrabold text-amber-800 mb-6 border-b pb-2 font-[Cinzel]">
        Privacy Policy
      </h1>
      <p className="text-gray-600 mb-4">
        Last Updated: October 2025
      </p>
      
      <div className="space-y-6 text-gray-700">
        <p>
          At <span className="text-amber-800 font-bold">Sardar Jewellers</span>, accessible from our website, one of our main priorities is the privacy of our visitors. This Privacy Policy outlines the types of information we collect, how we use it, and how we keep it secure.
        </p>
        <p>
          By using our website, you hereby consent to our Privacy Policy and agree to its terms. This policy applies only to our online activities and is valid for visitors to our website with regards to the information that they shared and/or collect.
        </p>

        <h2 className="text-2xl font-semibold text-amber-700 mt-6">
          Information We Collect
        </h2>
        <p>
          We collect information you provide directly to us when you make a purchase, sign up for an account, or contact us. This may include:
        </p>
        <ul className="list-disc ml-6 space-y-2">
            <li><strong>Personal Identification Details :</strong> Your name, address, email address, and phone number.</li>
            <li><strong>Communication Data :</strong> The contents of the messages and/or attachments you send us.</li>
            <li><strong>Account Information :</strong> If you register for an account (essential for accessing unique pricing/designs as a wholesaler/manufacturer), we collect your company name, address, and contact details.</li>
        </ul>

        <h2 className="text-2xl font-semibold text-amber-700 mt-6">
          How We Use Your Information
        </h2>
        <p>
          We use the information we collect in various ways, including to:
        </p>
        <ul className="list-disc ml-6 space-y-2">
            <li>Provide, operate, and maintain our website and services.</li>
            <li>Process and fulfill your purchase orders.</li>
            <li>Improve, personalize, and expand our offerings, products, and services.</li>
            <li>Communicate with you directly, including for customer service, updates, and marketing/promotional purposes.</li>
            <li>Find and prevent fraud or unauthorized activities.</li>
        </ul>
        
        <h2 className="text-2xl font-semibold text-amber-700 mt-6">
          Log Files and Cookies
        </h2>
        <p>
          Like any other website, <span className="text-amber-800 font-bold">Sardar Jewellers</span> uses <strong>Log Files</strong> and <strong>Cookies</strong>.
        </p>
        <ul className="list-disc ml-6 space-y-2">
            <li><strong>Log Files :</strong> These files log visitors when they visit websites. Information collected includes IP addresses, browser type, ISP, date/time stamp, referring/exit pages, and possibly the number of clicks. This data is used for analyzing trends, administering the site, and gathering demographic information.</li>
            <li><strong>Cookies :</strong> These are used to store information including visitors' preferences and the pages on the website that the visitor accessed or visited. The information is used to optimize the user's experience by customizing our web page content based on browser type and other data.</li>
        </ul>

        <h2 className="text-2xl font-semibold text-amber-700 mt-6">
          Payment Policy
        </h2>
        <p>
          We use a <strong>third-party payment gateway service</strong> (e.g., Stripe, PayPal, etc.) to securely collect and process payments. <strong>We never save or store your payment information, such as bank details or card details, in our database.</strong> All sensitive payment data is handled directly by the secure, compliant third-party gateway.
        </p>
        
        <h2 className="text-2xl font-semibold text-amber-700 mt-6">
          Permissions for Mobile App Usage (If Applicable)
        </h2>
        <p>
          If you use the <span className="text-amber-800 font-bold">Sardar Jewellers</span> mobile application, we may ask for the following permissions to ensure full functionality:
        </p>
        <ul className="list-disc ml-6 space-y-2">
            <li><strong>File Read/Write :</strong> Needed to download our high-resolution product images for offline viewing and quick access.</li>
            <li><strong>Notification Access :</strong> Required to send you alerts about new product arrivals, order status, and important updates.</li>
            <li><strong>Photo Library Access :</strong> Required to allow you to easily share product photos with others.</li>
        </ul>
        
        <h2 className="text-2xl font-semibold text-amber-700 mt-6">
          How to Delete Your Data
        </h2>
        <p>
          We are committed to providing you with control over your personal data. If you wish to delete your data from the <span className="text-amber-800 font-bold">Sardar Jewellers</span> app or website, please follow these steps:
        </p>
        <ol className="list-decimal ml-6 space-y-2">
            <li><strong>App Users :</strong> Navigate to the "Profile" section within the app and look for the "Delete Data" option (typically at the bottom of the screen). Follow the on-screen prompts to confirm the deletion.</li>
            <li><strong>Website/General Users :</strong> Please contact us directly at <a href="mailto:privacy@sardarjewellers.com" className="text-amber-700 hover:underline font-medium">privacy@sardarjewellers.com</a>. We will process your request to delete your personal data from our systems, subject to any legal retention requirements.</li>
        </ol>

        <h2 className="text-2xl font-semibold text-amber-700 mt-6">
          Contact Us
        </h2>
        <p>
          If you have additional questions or require more information about our Privacy Policy, do not hesitate to contact us.
        </p>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;